/**
 * Announcement Bar Pro JavaScript
 * Handles rotating messages and countdown timer functionality
 */

class AnnouncementBarPro {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      autoRotate: options.autoRotate !== undefined ? options.autoRotate : true,
      rotationInterval: options.interval || 5000,
      countdownEndDate: options.endDate,
      ...options
    };
    
    this.currentIndex = 0;
    this.messages = [];
    this.indicators = [];
    this.isPlaying = false;
    this.rotationTimer = null;
    this.countdownTimer = null;
    this.isPaused = false;
    
    this.init();
  }
  
  init() {
    this.cacheElements();
    this.setupMessages();
    this.setupControls();
    this.setupCountdown();
    this.setupCloseButton();
    this.setupAccessibility();
    
    if (this.options.autoRotate && this.messages.length > 1) {
      this.startRotation();
    }
    
    // Handle visibility change to pause/resume
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });
  }
  
  cacheElements() {
    this.messagesContainer = this.element.querySelector('.announcement-bar-pro__messages');
    this.messages = Array.from(this.element.querySelectorAll('.announcement-message'));
    this.indicators = Array.from(this.element.querySelectorAll('.indicator'));
    this.prevBtn = this.element.querySelector('.prev-btn');
    this.nextBtn = this.element.querySelector('.next-btn');
    this.controlsContainer = this.element.querySelector('.announcement-bar-pro__controls');
    this.closeBtn = this.element.querySelector('.announcement-bar-pro__close');
    this.countdownElements = {
      days: this.element.querySelector('[data-days]'),
      hours: this.element.querySelector('[data-hours]'),
      minutes: this.element.querySelector('[data-minutes]'),
      seconds: this.element.querySelector('[data-seconds]')
    };
  }
  
  setupMessages() {
    if (this.messages.length === 0) return;
    
    // Set initial state
    this.messages.forEach((message, index) => {
      message.classList.toggle('active', index === 0);
      message.setAttribute('aria-hidden', index !== 0);
    });
    
    // Add hover pause functionality
    if (this.messagesContainer) {
      this.messagesContainer.addEventListener('mouseenter', () => this.pause());
      this.messagesContainer.addEventListener('mouseleave', () => this.resume());
    }
  }
  
  setupControls() {
    if (!this.controlsContainer || this.messages.length <= 1) return;
    
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToPrevious());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToNext());
    }
    
    // Indicator buttons
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => {
      if (e.target.closest('.announcement-bar-pro__controls')) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.goToPrevious();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.goToNext();
            break;
          case ' ':
          case 'Enter':
            if (e.target.classList.contains('indicator')) {
              e.preventDefault();
              const index = parseInt(e.target.dataset.index);
              this.goToSlide(index);
            }
            break;
        }
      }
    });
  }
  
  setupCountdown() {
    if (!this.options.countdownEndDate || !this.countdownElements.days) return;
    
    const endDate = new Date(this.options.countdownEndDate);
    if (isNaN(endDate.getTime())) {
      console.warn('Invalid countdown end date:', this.options.countdownEndDate);
      return;
    }
    
    this.updateCountdown(endDate);
    this.countdownTimer = setInterval(() => {
      this.updateCountdown(endDate);
    }, 1000);
  }
  
  updateCountdown(endDate) {
    const now = new Date().getTime();
    const distance = endDate.getTime() - now;
    
    if (distance < 0) {
      this.stopCountdown();
      return;
    }
    
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    
    if (this.countdownElements.days) {
      this.countdownElements.days.textContent = days.toString().padStart(2, '0');
    }
    if (this.countdownElements.hours) {
      this.countdownElements.hours.textContent = hours.toString().padStart(2, '0');
    }
    if (this.countdownElements.minutes) {
      this.countdownElements.minutes.textContent = minutes.toString().padStart(2, '0');
    }
    if (this.countdownElements.seconds) {
      this.countdownElements.seconds.textContent = seconds.toString().padStart(2, '0');
    }
  }
  
  stopCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    
    // Hide countdown or show expired message
    const countdownContainer = this.element.querySelector('.announcement-bar-pro__countdown');
    if (countdownContainer) {
      countdownContainer.style.opacity = '0.5';
      countdownContainer.setAttribute('aria-label', 'Countdown expired');
    }
  }
  
  setupCloseButton() {
    if (!this.closeBtn) return;
    
    this.closeBtn.addEventListener('click', () => {
      this.close();
    });
  }
  
  setupAccessibility() {
    // Set up proper ARIA attributes
    if (this.messagesContainer) {
      this.messagesContainer.setAttribute('role', 'region');
      this.messagesContainer.setAttribute('aria-live', 'polite');
    }
    
    // Set up indicator roles
    this.indicators.forEach((indicator, index) => {
      indicator.setAttribute('role', 'tab');
      indicator.setAttribute('aria-controls', `message-${index}`);
      indicator.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
    
    // Set up message IDs
    this.messages.forEach((message, index) => {
      message.setAttribute('id', `message-${index}`);
      message.setAttribute('role', 'tabpanel');
    });
  }
  
  goToSlide(index) {
    if (index === this.currentIndex || index < 0 || index >= this.messages.length) return;
    
    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    
    // Update messages
    this.messages.forEach((message, i) => {
      message.classList.toggle('active', i === index);
      message.setAttribute('aria-hidden', i !== index);
    });
    
    // Update indicators
    this.indicators.forEach((indicator, i) => {
      indicator.classList.toggle('active', i === index);
      indicator.setAttribute('aria-selected', i === index);
      indicator.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    
    // Announce change to screen readers
    if (this.messagesContainer) {
      this.messagesContainer.setAttribute('aria-label', 
        `Announcement ${index + 1} of ${this.messages.length}`);
    }
    
    // Reset rotation timer if auto-rotating
    if (this.options.autoRotate && this.isPlaying) {
      this.resetRotationTimer();
    }
  }
  
  goToNext() {
    const nextIndex = (this.currentIndex + 1) % this.messages.length;
    this.goToSlide(nextIndex);
  }
  
  goToPrevious() {
    const prevIndex = (this.currentIndex - 1 + this.messages.length) % this.messages.length;
    this.goToSlide(prevIndex);
  }
  
  startRotation() {
    if (this.messages.length <= 1 || this.isPlaying) return;
    
    this.isPlaying = true;
    this.rotationTimer = setInterval(() => {
      if (!this.isPaused) {
        this.goToNext();
      }
    }, this.options.rotationInterval);
  }
  
  stopRotation() {
    this.isPlaying = false;
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }
  }
  
  resetRotationTimer() {
    if (this.isPlaying) {
      this.stopRotation();
      this.startRotation();
    }
  }
  
  pause() {
    this.isPaused = true;
  }
  
  resume() {
    this.isPaused = false;
  }
  
  close() {
    this.element.classList.add('hidden');
    this.stopRotation();
    this.stopCountdown();
    
    // Store closed state in session storage if available
    try {
      sessionStorage.setItem('announcement-bar-closed', 'true');
    } catch (e) {
      // Fallback if sessionStorage is not available
      document.cookie = 'announcement-bar-closed=true; path=/; max-age=86400';
    }
    
    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('announcementbar:closed', {
      bubbles: true
    }));
  }
  
  show() {
    this.element.classList.remove('hidden');
    if (this.options.autoRotate && this.messages.length > 1) {
      this.startRotation();
    }
  }
  
  destroy() {
    this.stopRotation();
    this.stopCountdown();
    
    // Remove event listeners would go here if we were tracking them
    // For simplicity, we're not implementing full cleanup in this example
  }
}

// Auto-initialize announcement bars
document.addEventListener('DOMContentLoaded', function() {
  const announcementBars = document.querySelectorAll('.announcement-bar-pro');
  
  announcementBars.forEach(bar => {
    const sectionId = bar.id;
    const config = window.AnnouncementBarPro && window.AnnouncementBarPro[sectionId];
    
    if (config) {
      // Check if bar was previously closed
      let wasClosed = false;
      try {
        wasClosed = sessionStorage.getItem('announcement-bar-closed') === 'true';
      } catch (e) {
        // Fallback to check cookie
        wasClosed = document.cookie.includes('announcement-bar-closed=true');
      }
      
      if (!wasClosed) {
        new AnnouncementBarPro(bar, {
          autoRotate: config.autoRotate,
          interval: config.interval * 1000, // Convert to milliseconds
          endDate: config.endDate
        });
      } else {
        bar.classList.add('hidden');
      }
    }
  });
});

// Utility functions for external control
window.AnnouncementBarProUtils = {
  // Get instance by section ID
  getInstance: function(sectionId) {
    const element = document.getElementById(sectionId);
    return element ? element.announcementBarInstance : null;
  },
  
  // Show all hidden announcement bars
  showAll: function() {
    document.querySelectorAll('.announcement-bar-pro.hidden').forEach(bar => {
      bar.classList.remove('hidden');
      if (bar.announcementBarInstance) {
        bar.announcementBarInstance.show();
      }
    });
    
    // Clear closed state
    try {
      sessionStorage.removeItem('announcement-bar-closed');
    } catch (e) {
      document.cookie = 'announcement-bar-closed=; path=/; max-age=0';
    }
  },
  
  // Hide all announcement bars
  hideAll: function() {
    document.querySelectorAll('.announcement-bar-pro').forEach(bar => {
      if (bar.announcementBarInstance) {
        bar.announcementBarInstance.close();
      }
    });
  }
};

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AnnouncementBarPro;
} else if (typeof define === 'function' && define.amd) {
  define(function() {
    return AnnouncementBarPro;
  });
} else {
  window.AnnouncementBarPro = AnnouncementBarPro;
}


